import {
  Dimmer,
  DimmerDimmable,
  Divider,
  Form,
  Header,
  Loader,
  Message,
  Modal,
  Segment,
} from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { ModalConfirm } from '@/components/Autre';
import {
  BoutonsValidationEncaissement,
  ButtonAjoutReglement,
  CommentairesEncaissement,
  MessageDepassementTitres,
} from '@/components/Encaissement/ModalEncaissementCommun';
import { ENCAISSEMENT } from '@/constants';
import { ENCAISSEMENT_PARTIEL, IMPAYE_PARTIEL } from '@/constants/etatFacture';
// Import par chemin direct et non via le barrel `@/hooks` : cela évite de charger les 17 hooks
// (et leurs dépendances) dans les tests de cette modale.
import useEtatMFO from '@/hooks/useEtatMFO';
import useReglementsEncaissement from '@/hooks/useReglementsEncaissement';
import type { FactureType } from '@/types';
import {
  authorizeRepartAuto,
  avecCodeModeReglement,
  formatReglementBody,
  getInfosEncaissementDirect,
  repartirAuProrata,
  verifierEligibiliteTitres,
} from '@/utils';
import { getMessageErreurEncaissement } from '@/utils/apiErrors';
import { useRegieStore } from '@/zustand';
import { ModalAffectationReglement } from './ModalAffectationReglement';
import ModalEncaissementReglementPartiel from './ModalEncaissementReglementPartiel';
import { ModalEncaissementReglements } from './ModalEncaissementReglements';
import { ModalEncaissementTable } from './ModalEncaissementTable';
import ModalFactureDejaPayee from './ModalFactureDejaPayee';
import { ReglementTiers } from './ReglementTiers';

interface ModalEncaissementProps {
  // Identifiant de la facture sélectionnée
  identifiantFacture?: number;
  // Liste des factures
  factures: FactureType[];
  // Affiche ou non le bouton d'envoi du reçu par mail
  envoiRecuParMail?: boolean;
  // Active ou non le reglement par un tiers
  reglementTiers?: boolean;
  // Callback a la validation de la modal
  onSave: (
    body: Record<string, unknown>,
    imprimerRecu?: boolean,
    envoyerMail?: boolean,
  ) => Promise<void>;

  // Callback à la fermeture de la modal
  onClose?: () => void;
  // S'affiche en mode LOOC
  looc?: boolean;
}

type FactureSelectionType = { identifiant: number; checked: boolean };
type TotalType = {
  // montant total
  facture: number;
  // montant restant en fonction des factures sélectionnées
  reste: number;
  // montant restant en prenant en compte les règlements
  resteReglements: number;
  // montant total payé en numéraire
  numeraire: number;
};

const ModalEncaissement = (props: ModalEncaissementProps) => {
  const {
    identifiantFacture,
    factures,
    envoiRecuParMail,
    reglementTiers = false,
    looc = false,
    onSave,
    onClose,
  } = props;

  const encaissementPossible = useRegieStore((state) => state.regie?.encaissementPossible);

  // #region Declaration
  const embeddedInfos = getInfosEncaissementDirect();

  // État contenant le tiers qui règle l'encaissement.
  // Contrôle du paiement en ligne : porte sur TOUTES les factures affichées, dès qu'elles
  // arrivent — le parent les charge de façon asynchrone. Le régisseur peut ensuite cocher
  // n'importe laquelle sans déclencher de nouvel appel.
  const identifiantsFactures = React.useMemo(() => _.map(factures, 'identifiant'), [factures]);
  const { facturesPayees, mfoIndisponible, verifierMaintenant } = useEtatMFO(identifiantsFactures);

  const facturesDejaPayees = React.useMemo(
    () => _.filter(factures, (facture) => facturesPayees.has(facture.identifiant)),
    [factures, facturesPayees],
  );

  const [tiers, setTiers] = React.useState();
  // { identifiant: newIdentifiant(), nom: 'Dupont', prenom: 'Jacques' },
  // État contenant les identifiants des factures sélectionnées
  const [selectedFactures, setSelectedFactures] = React.useState<number[]>([
    identifiantFacture || embeddedInfos?.identifiantFacture,
  ]);
  // État indiquant si le modal de règlement partiel est ouvert ou fermé.
  const [modalReglementPartiel, setModalReglementPartiel] = React.useState<boolean>(false);
  // État indiquant si le modal d'affectation de règlement est ouvert ou fermé.
  const [modalAffectationReglement, setModalAffectationReglement] = React.useState<boolean>(false);
  // État indiquant si l'encaissement est prêt a être valider
  const [isComplete, setIsComplete] = React.useState<boolean>(false);
  // Etat de chargement
  const [loading, setLoading] = React.useState<boolean>(_.isEmpty(factures) || false);
  // Commentaire du reçu
  const [commentaire, setCommentaire] = React.useState<string>('');
  // Commentaire du payeur
  const [commentairePayeur, setCommentairePayeur] = React.useState<string>(
    _.head(factures)?.payeur?.commentaire,
  );
  /** Confirmation de sélection de facture si on a déjà saisi des règlements */
  const [factureSelectionConfirm, setFactureSelectionConfirm] =
    React.useState<FactureSelectionType>();
  // État pour conserver l'information d'impression du reçu
  const [shouldPrintRecu, setShouldPrintRecu] = React.useState<boolean>(false);
  // État pour conserver l'information d'envoi du reçu par mail
  const [shouldSendMail, setShouldSendMail] = React.useState<boolean>(false);
  /** Factures sélectionnées que MFO déclare payées au moment de la validation : encaissement bloqué */
  const [facturesBloquantes, setFacturesBloquantes] = React.useState<FactureType[]>(null);

  /**
   * Montants issus des factures sélectionnées : total facturé et reste à encaisser.
   * Le reste est conservé non arrondi, car c'est lui qui sert de base au calcul du
   * reste après règlements.
   */
  const montantFactures = React.useMemo(() => {
    let facture = 0;
    let reste = 0;

    _.forEach(
      _.filter(factures, (f) => _.includes(selectedFactures, f.identifiant)),
      (f) => {
        facture += _.sumBy(f.lignes, 'montant');
        reste += _.sumBy(f.lignes, 'reste');
      },
    );

    return { facture, reste };
  }, [factures, selectedFactures]);

  const {
    reglements,
    filteredModesReglements,
    reglementsComplet,
    resteModeReglementDisponible,
    montantNumeraire,
    resteReglements,
    handleReglementResetAll,
    handleReglementChange,
    handleReglementReset,
    handleReglementMultipleDelete,
    handleReglementMultipleAdd,
    getModesReglementsDesactives,
  } = useReglementsEncaissement(montantFactures.reste);

  /**
   * Objet contenant :
   *  - montant total
   *  - montant restant en fonction des factures sélectionnées
   *  - montant restant en prenant en compte les règlements
   *  - montant total payé en numéraire
   */
  const total: TotalType = React.useMemo(
    () => ({
      facture: montantFactures.facture,
      reste: parseFloat(montantFactures.reste.toFixed(2)),
      resteReglements,
      numeraire: montantNumeraire,
    }),
    [montantFactures.facture, montantFactures.reste, resteReglements, montantNumeraire],
  );

  /**
   * Lignes des factures sélectionnées, servies par la répartition au prorata de leur reste.
   * Leur produit porte l'éligibilité aux titres de paiement (CESU, chèque vacances).
   */
  const lignesRepartition = React.useMemo(
    () =>
      _.map(
        _.flatMap(
          _.filter(factures, (facture) => _.includes(selectedFactures, facture.identifiant)),
          'lignes',
        ),
        (ligne) => ({ cle: ligne.identifiant, montant: ligne.reste, produit: ligne.produit }),
      ),
    [factures, selectedFactures],
  );

  // Règlements complétés du code de leur mode, pour réserver les titres aux produits éligibles.
  const reglementsRepartition = React.useMemo(
    () => avecCodeModeReglement(reglements, filteredModesReglements),
    [reglements, filteredModesReglements],
  );

  // Titres de paiement supérieurs au total des produits éligibles : la validation est bloquée.
  const depassementsTitres = React.useMemo(
    () => verifierEligibiliteTitres(lignesRepartition, reglementsRepartition),
    [lignesRepartition, reglementsRepartition],
  );

  const payeur = React.useMemo<string>(() => {
    if (_.isEmpty(factures)) {
      return '';
    }

    const { payeur: p } = _.head(factures);
    const { civilite } = p || {};

    return `${civilite?.abreviation || ''} ${p.nom} ${p.prenom}`;
  }, [factures]);

  const isEncaissementButtonDisabled = React.useMemo(
    () =>
      encaissementPossible === false ||
      !reglementsComplet ||
      total.resteReglements > total.reste ||
      total.resteReglements < 0 ||
      total.numeraire > ENCAISSEMENT.PLAFOND_NUMERAIRE ||
      !_.isEmpty(depassementsTitres) ||
      loading,
    [
      encaissementPossible,
      reglementsComplet,
      total.resteReglements,
      total.reste,
      total.numeraire,
      depassementsTitres,
      loading,
    ],
  );

  // S'il y a au moins un reglement numéraire, on n'autorise pas de faire un encaissement sans reçu
  const isEncaissementSansRecuDisabled = React.useMemo(() => {
    // Flemme de réadapter tous les tests juste pour cette petite modif
    if (total.numeraire > 0 && process.env.NODE_ENV !== 'test') {
      return true;
    }

    return isEncaissementButtonDisabled;
  }, [total.numeraire, isEncaissementButtonDisabled]);

  const isEncaissementButtonPositive = React.useMemo(
    () =>
      reglementsComplet &&
      total.resteReglements <= total.reste &&
      total.resteReglements >= 0 &&
      _.isEmpty(depassementsTitres),
    [reglementsComplet, total.resteReglements, total.reste, depassementsTitres],
  );

  /** On autorise l'ajout s'il y a au moins une facture sélectionnée et pas plus
   * et qu'il reste au moins un mode de règlement disponible */
  const addButtonVisible = React.useMemo(
    () =>
      !_.isEmpty(selectedFactures) && _.size(selectedFactures) <= 1 && resteModeReglementDisponible,
    [resteModeReglementDisponible, selectedFactures],
  );

  // #endregion

  // #region Fonctions

  /**
   * Gère la sélection d'une facture, qui sera prise en compte ou non pour le(s) règlement(s).
   *
   * @param {boolean} checked - Indicates whether the facture is checked or unchecked.
   * @param {number} identifiant - The identifier of the facture.
   */
  const handleFactureSelected = React.useCallback(
    (checked, identifiant = 0) => {
      if (identifiant === 0) {
        setSelectedFactures(!checked ? [] : _.map(factures, (facture) => facture.identifiant));
      } else if (checked) {
        setSelectedFactures((prev) => [...prev, identifiant]);
      } else {
        setSelectedFactures((prev) => _.filter(prev, (p) => p !== identifiant));
      }

      // Quand on sélectionne une facture, on doit remettre complètement à 0 tous les règlements
      // car on peut potentiellement ne plus avoir aucune facture sélectionnée
      if (_.some(reglements, { complet: true })) {
        handleReglementResetAll();
      }

      setFactureSelectionConfirm(null);
    },
    [factures, handleReglementResetAll, reglements],
  );

  /**
   * Demande confirmation à l'utilisateur si besoin lors de la sélection / déselection d'une facture
   *
   * @param {boolean} checked - Indique si la facture est sélectionnée ou non.
   * @param {number} identifiant - L'identifiant de la facture.
   */
  const handleFactureSelect = React.useCallback(
    (checked, identifiant = 0) => {
      // Si on a au moins un règlement complet, on demande confirmation
      // car on doit le RAZ
      if (_.some(reglements, { complet: true })) {
        setFactureSelectionConfirm({ checked, identifiant });
      } else {
        // Sinon on le laisse faire ce qu'il veut
        handleFactureSelected(checked, identifiant);
      }
    },
    [handleFactureSelected, reglements],
  );

  /**
   * Construit le corps de la requête d'encaissement.
   */
  const getBodyEncaissement = React.useCallback(
    (all = true, data = []) => {
      const facturesSelected = _.filter(factures, (facture) =>
        _.includes(selectedFactures, facture.identifiant),
      );

      // Répartition au prorata du reste de chaque ligne (branche automatique uniquement), les
      // titres de paiement n'étant affectés qu'aux produits éligibles.
      const tabCalculeProrata = all
        ? repartirAuProrata(lignesRepartition, reglementsRepartition, total.reste)
        : {};

      const body = {
        // !TODO : Gestion documents
        identifiantDocInfo: 1,
        identifiantAdresse: _.head(facturesSelected)?.payeur?.adresse?.identifiant,
        commentaire,
        commentairePayeur,
        // On le reverse pour que le premier règlement saisi ressorte en premier dans les reçus
        reglements: _.reverse(
          _.map(reglements, (reglement) => {
            let lignesFacture = [];

            if (all) {
              lignesFacture = _.map(_.flatMap(facturesSelected, 'lignes'), (ligne) => ({
                identifiantLigneFacture: ligne?.identifiant,
                montantReglement: tabCalculeProrata[ligne.identifiant][reglement.identifiant],
              }));
            } else {
              _.forEach(
                _.filter(
                  data,
                  (d) =>
                    d.montant !== '' &&
                    d.montant !== 0 &&
                    d.identifiantReglement === reglement.identifiant,
                ),
                (ligne) => {
                  const ligneFacture = _.find(_.flatMap(facturesSelected, 'lignes'), {
                    identifiant: ligne.identifiantLigne,
                  });

                  lignesFacture.push({
                    identifiantLigneFacture: ligneFacture?.identifiant,
                    montantReglement: ligne.montant,
                  });
                },
              );
            }

            return {
              // !TODO: Paiement par un tiers
              ...formatReglementBody(reglement, _.head(facturesSelected)?.identifiantPayeur),
              lignesFacture,
            };
          }),
        ),
      };

      return body;
    },
    [
      factures,
      reglements,
      selectedFactures,
      lignesRepartition,
      reglementsRepartition,
      total.reste,
      commentaire,
      commentairePayeur,
    ],
  );

  /**
   * Gère la validation d'un règlement simple.
   *
   * @param {string} param - Le paramètre pour indiquer l'action à effectuer après la validation.
   */
  const handleValidate = React.useCallback(
    async (param?: string) => {
      // Second contrôle MFO, avant toute ramification. Il est ici et non juste avant `onSave` parce
      // que les deux branches ci-dessous sortent de la fonction en ouvrant une modale de règlement
      // partiel : la sauvegarde repart alors de `handleSelectReglementPartiel` ou de
      // `handleValidateAffectation`, qui ne repasseraient jamais par une garde placée plus bas.
      //
      // On interroge MFO sur toutes les factures affichées — c'est la portée du bandeau, que
      // `verifierMaintenant` rafraîchit au passage — mais seules les factures **sélectionnées**
      // bloquent : une facture payée que le régisseur n'encaisse pas ne le concerne pas.
      setLoading(true);
      const { payees, indisponible } = await verifierMaintenant(identifiantsFactures);
      // Rabaissé tout de suite : les branches ci-dessous gèrent `loading` comme avant, et un Dimmer
      // resté actif figerait la modale de règlement partiel qu'elles ouvrent.
      setLoading(false);

      // MFO muet : on laisse encaisser. Bloquer la caisse sur une panne d'un service tiers coûte
      // plus cher que le doublon de paiement qu'on cherche à éviter, et le bandeau prévient déjà.
      if (!indisponible) {
        const bloquantes = _.filter(
          factures,
          (facture) =>
            _.includes(selectedFactures, facture.identifiant) && payees.has(facture.identifiant),
        );

        if (!_.isEmpty(bloquantes)) {
          setFacturesBloquantes(bloquantes);

          return;
        }
      }

      let save = false;
      const imprimerRecu = param === 'print';
      const envoyerMail = param === 'mail';
      setShouldPrintRecu(imprimerRecu);
      setShouldSendMail(envoyerMail);

      const totalSaisie = _.sumBy(reglements, 'montant');
      if (total.reste !== parseFloat(totalSaisie.toFixed(2)) && !isComplete) {
        if (selectedFactures.length === 1) {
          const { lignes } = _.find(
            factures,
            (facture) => selectedFactures[0] === facture.identifiant,
          );

          if (lignes.length === 1) {
            setLoading(true);
            save = true;
          } else {
            setModalReglementPartiel(true);
          }
        } else {
          setModalReglementPartiel(true);
        }
      } else {
        setLoading(true);

        save = true;
      }

      if (save) {
        const body = getBodyEncaissement();
        if (onSave) {
          onSave(body, imprimerRecu, envoyerMail)
            .then(() => {
              setSelectedFactures([]);
            })
            .catch((error) => {
              toast(getMessageErreurEncaissement(error), { type: 'error' });
            })
            .finally(() => {
              setLoading(false);
            });
        }
      }
    },
    [
      factures,
      getBodyEncaissement,
      identifiantsFactures,
      isComplete,
      onSave,
      reglements,
      selectedFactures,
      total.reste,
      verifierMaintenant,
    ],
  );

  /**
   * Gère la sélection du règlement partiel.
   *
   * @param {boolean} auto - Indique si le règlement partiel est automatique ou non.
   */
  const handleSelectReglementPartiel = (auto?: boolean) => {
    setModalReglementPartiel(false);
    if (auto) {
      const body = getBodyEncaissement();

      if (onSave) {
        setIsComplete(true);
        setLoading(true);

        onSave(body, shouldPrintRecu, shouldSendMail)
          .then(() => {
            setSelectedFactures([]);
          })
          .catch((error) => {
            toast(getMessageErreurEncaissement(error), { type: 'error' });
          })
          .finally(() => {
            setLoading(false);
          });
      }
      // Effectuer les actions nécessaires pour le règlement partiel automatique
    } else {
      setModalAffectationReglement(true);
      // Effectuer les actions nécessaires pour le règlement partiel manuel
    }
  };

  /**
   * Valide l'affectation des règlements.
   *
   * @param {Array} data - Les données de l'affectation des règlements.
   */
  const handleValidateAffectation = (data: Record<string, number | string>[]) => {
    setIsComplete(true);
    setLoading(true);

    const body = getBodyEncaissement(false, data);

    if (onSave) {
      onSave(body, shouldPrintRecu, shouldSendMail)
        .then(() => {
          setSelectedFactures([]);
          setModalAffectationReglement(false);
        })
        .catch((error) => {
          toast(getMessageErreurEncaissement(error), { type: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // #endregion

  React.useEffect(() => {
    setIsComplete(false);
  }, [reglements]);

  React.useEffect(() => {
    if (!_.isEmpty(factures)) {
      setLoading(false);

      setCommentairePayeur(_.head(factures)?.payeur?.commentaire);
    }
  }, [factures]);

  return (
    <>
      <Modal
        open
        {...(onClose && { closeIcon: true })}
        {...((!loading || _.isEmpty(factures)) && onClose && { onClose })}
        size="huge"
      >
        {looc ? (
          <Modal.Header className="looc">
            Encaissement
            {!_.isEmpty(factures) &&
              { Solde: ' du solde', Acompte: " de l'acompte", Caution: ' de la caution' }[
                _.head(factures).typeProduit ?? 'Acompte'
              ]}
          </Modal.Header>
        ) : (
          <Modal.Header>Encaissement de facture</Modal.Header>
        )}
        <Modal.Content>
          <DimmerDimmable as={Form} dimmed={loading} style={{ width: '100%', minHeight: 150 }}>
            <Dimmer active={loading} inverted>
              <Loader
                inverted
                content={
                  _.isEmpty(factures)
                    ? 'Chargement des factures du payeur'
                    : 'Encaissement de(s) facture(s) en cours'
                }
              />
            </Dimmer>
            {!_.isEmpty(factures) && (
              <>
                {!_.isEmpty(facturesDejaPayees) ? (
                  <Message warning size="small" data-testid="message-factures-payees-mfo">
                    <Message.Header>
                      {facturesDejaPayees.length > 1
                        ? 'Ces factures ont déjà été payées en ligne'
                        : 'Cette facture a déjà été payée en ligne'}
                    </Message.Header>
                    <Message.List>
                      {_.map(facturesDejaPayees, (facture) => (
                        <Message.Item key={facture.identifiant}>{facture.numero}</Message.Item>
                      ))}
                    </Message.List>
                  </Message>
                ) : null}
                {mfoIndisponible ? (
                  <Message info size="small" data-testid="message-mfo-indisponible">
                    <Message.Header>Contrôle du paiement en ligne indisponible</Message.Header>
                    <p>
                      {
                        "Le paiement en ligne n'a pas pu être vérifié. L'encaissement reste possible."
                      }
                    </p>
                  </Message>
                ) : null}
                <ModalEncaissementTable
                  looc={looc}
                  reste={total.reste}
                  factures={factures}
                  selected={selectedFactures}
                  onSelect={handleFactureSelect}
                />
                {!_.isEmpty(selectedFactures) ? (
                  <>
                    <ModalEncaissementReglements
                      looc={looc}
                      reglements={reglements}
                      payeur={payeur}
                      reste={total.reste}
                      reglementsComplet={reglementsComplet}
                      onReglementMultipleDelete={handleReglementMultipleDelete}
                      onReglementChange={handleReglementChange}
                      onReglementReset={handleReglementReset}
                      addButtonVisible={addButtonVisible}
                      getModesReglementsDesactives={getModesReglementsDesactives}
                    >
                      <ButtonAjoutReglement
                        testId="button-add-reglement"
                        visible={
                          _.size(reglements) < ENCAISSEMENT.NOMBRE_REGLEMENTS_MAX &&
                          total.reste !== total.resteReglements &&
                          total.resteReglements > 0 &&
                          reglementsComplet
                        }
                        maximumAtteint={_.size(reglements) === ENCAISSEMENT.NOMBRE_REGLEMENTS_MAX}
                        onAdd={handleReglementMultipleAdd}
                      />
                    </ModalEncaissementReglements>
                    <MessageDepassementTitres depassements={depassementsTitres} />
                    <Divider />
                    <Segment basic style={{ marginTop: 0, paddingBottom: 0, paddingRight: 0 }}>
                      {reglementTiers && (
                        <Form.Group
                          inline
                          style={{
                            justifyContent: 'space-between',
                            alignItems: 'stretch',
                            marginBottom: '2.5em',
                          }}
                        >
                          <ReglementTiers tiers={tiers} onTiersChange={setTiers} />
                        </Form.Group>
                      )}
                      <CommentairesEncaissement
                        commentaire={commentaire}
                        onCommentaireChange={setCommentaire}
                        commentairePayeur={commentairePayeur}
                        onCommentairePayeurChange={setCommentairePayeur}
                        commentairePayeurInitial={_.head(factures)?.payeur?.commentaire}
                      />
                    </Segment>
                  </>
                ) : (
                  <Message info size="small">
                    <Message.Header>
                      Veuillez sélectionner une ou plusieurs factures à encaisser
                    </Message.Header>
                  </Message>
                )}
              </>
            )}
          </DimmerDimmable>
        </Modal.Content>
        <Modal.Actions>
          {!_.isEmpty(factures) && (
            <BoutonsValidationEncaissement
              loading={loading}
              positive={isEncaissementButtonPositive}
              onValidate={handleValidate}
              sansRecuVisible={!looc}
              sansRecuDisabled={isEncaissementSansRecuDisabled}
              avecRecuDisabled={isEncaissementButtonDisabled}
              mailVisible={envoiRecuParMail && !looc}
              onClose={onClose}
              retourContent="Retour aux factures à encaisser"
              testIds={{
                retour: 'button-cancel-encaissement',
                sansRecu: 'button-validate-sans-recu',
                mail: 'button-validate-avec-recu-mail',
                print: 'button-validate-avec-recu-print',
              }}
            />
          )}
        </Modal.Actions>
      </Modal>
      {modalReglementPartiel && (
        <ModalEncaissementReglementPartiel
          onSelect={handleSelectReglementPartiel}
          nombreFacture={_.size(selectedFactures)}
          total={total.reste}
          montant={_.sumBy(reglements, 'montant')}
          multiple={_.size(reglements) > 1}
          partiel={_.some(
            factures,
            (facture) =>
              _.includes(selectedFactures, facture.identifiant) &&
              [ENCAISSEMENT_PARTIEL, IMPAYE_PARTIEL].includes(facture.etat),
          )}
          onClose={() => {
            setModalReglementPartiel(false);
            setShouldPrintRecu(false);
            setShouldSendMail(false);
          }}
          disableRepartAuto={!authorizeRepartAuto(reglements, filteredModesReglements)}
        />
      )}
      {modalAffectationReglement && (
        <ModalAffectationReglement
          loading={loading}
          factures={_.filter(factures, (facture) =>
            _.includes(selectedFactures, facture.identifiant),
          )}
          onValidate={handleValidateAffectation}
          reglements={reglements}
          montant={_.sumBy(reglements, 'montant')}
          onClose={() => {
            setModalReglementPartiel(true);
            setModalAffectationReglement(false);
            setShouldPrintRecu(false);
            setShouldSendMail(false);
          }}
        />
      )}
      {!_.isEmpty(facturesBloquantes) && (
        <ModalFactureDejaPayee
          factures={facturesBloquantes}
          onClose={() => setFacturesBloquantes(null)}
        />
      )}
      {factureSelectionConfirm && (
        <ModalConfirm
          size="tiny"
          content={
            <Header as="h4">
              <Header.Content>
                Êtes-vous sûr de vouloir modifier les factures sélectionnées ?
                <br />
                <br />
                <Header.Subheader>
                  Les règlements préalablement saisis devront être effacés.
                </Header.Subheader>
              </Header.Content>
            </Header>
          }
          onConfirm={() =>
            handleFactureSelected(
              factureSelectionConfirm.checked,
              factureSelectionConfirm.identifiant,
            )
          }
          onCancel={() => setFactureSelectionConfirm(null)}
        />
      )}
    </>
  );
};

export default ModalEncaissement;
